import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Plus, Edit, Trash2, CheckCircle, ChevronDown, ChevronRight, User, Plane, Hotel, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/contexts/AuthContext';
import { format, parseISO } from 'date-fns';

export default function CrewSection({ eventId, permissions }: { eventId: string; permissions: any }) {
  const { user } = useAuth();
  const [crew, setCrew] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [registeredUsers, setRegisteredUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<any>({
    role: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company: '',
    permission_level: 'viewer',
    hotel: '',
    check_in: '',
    check_out: '',
    room_type: '',
    confirmation: '',
    travel_days: '',
    arrival_flight: '',
    arrival_datetime: '',
    departure_flight: '',
    departure_datetime: '',
    travel_notes: '',
    emergency_contact: '',
    emergency_phone: '',
    notes: '',
    is_own_record: false
  });

  useEffect(() => {
    loadCrew();
  }, [eventId]);

  useEffect(() => {
    if (showForm && !editingId) {
      loadRegisteredUsers();
    }
  }, [showForm, editingId]);

  const loadCrew = async () => {
    const { data } = await supabase
      .from('crew_members')
      .select('*, user_id')
      .eq('event_id', eventId)
      .order('role');
    if (data) setCrew(data);
    setLoading(false);
  };

  const loadRegisteredUsers = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name, role')
      .order('full_name');
    if (data) setRegisteredUsers(data);
  };

  const handleUserSelect = async (userId: string) => {
    if (!userId) return;

    setSelectedUserId(userId);
    const user = registeredUsers.find(u => u.id === userId);
    if (!user) return;

    const nameParts = user.full_name ? user.full_name.split(' ') : ['', ''];
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    setFormData({
      ...formData,
      first_name: firstName,
      last_name: lastName,
      email: user.email || '',
      role: user.role || formData.role
    });
  };

  const handleAddNew = () => {
    setFormData({
      role: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
      permission_level: 'viewer',
      hotel: '',
      check_in: '',
      check_out: '',
      room_type: '',
      confirmation: '',
      travel_days: '',
      arrival_flight: '',
      arrival_datetime: '',
      departure_flight: '',
      departure_datetime: '',
      travel_notes: '',
      emergency_contact: '',
      emergency_phone: '',
      notes: '',
      is_own_record: false
    });
    setEditingId(null);
    setSelectedUserId('');
    setShowForm(true);
  };

  const handleEdit = (member: any) => {
    let firstName = member.first_name || '';
    let lastName = member.last_name || '';

    if (!firstName && !lastName && member.full_name) {
      const nameParts = member.full_name.split(' ');
      firstName = nameParts[0] || '';
      lastName = nameParts.slice(1).join(' ') || '';
    }

    const formatDateTime = (datetime: string | null) => {
      if (!datetime) return '';
      try {
        return format(parseISO(datetime), "yyyy-MM-dd'T'HH:mm");
      } catch {
        return '';
      }
    };

    setFormData({
      role: member.role || '',
      first_name: firstName,
      last_name: lastName,
      email: member.email || '',
      phone: member.phone || '',
      company: member.company || '',
      permission_level: member.permission_level || 'viewer',
      hotel: member.hotel || '',
      check_in: member.check_in || '',
      check_out: member.check_out || '',
      room_type: member.room_type || '',
      confirmation: member.confirmation || '',
      travel_days: member.travel_days || '',
      arrival_flight: member.arrival_flight || '',
      arrival_datetime: formatDateTime(member.arrival_datetime),
      departure_flight: member.departure_flight || '',
      departure_datetime: formatDateTime(member.departure_datetime),
      travel_notes: member.travel_notes || '',
      emergency_contact: member.emergency_contact || '',
      emergency_phone: member.emergency_phone || '',
      notes: member.notes || '',
      is_own_record: member.user_id === user?.id
    });
    setEditingId(member.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const fullName = `${formData.first_name || ''} ${formData.last_name || ''}`.trim();

      if (!fullName) {
        alert('Please enter at least a first or last name');
        setSaving(false);
        return;
      }

      if (!formData.role || !formData.role.trim()) {
        alert('Please enter a role/position');
        setSaving(false);
        return;
      }

      if (!editingId && !selectedUserId) {
        alert('Please select a user');
        setSaving(false);
        return;
      }

      const createISODateTime = (datetimeLocal: string) => {
        if (!datetimeLocal) return null;
        const [date, time] = datetimeLocal.split('T');
        const localDate = new Date(`${date}T${time}:00`);
        const offset = -localDate.getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offset) / 60).toString().padStart(2, '0');
        const offsetMinutes = (Math.abs(offset) % 60).toString().padStart(2, '0');
        const offsetSign = offset >= 0 ? '+' : '-';
        return `${date}T${time}:00${offsetSign}${offsetHours}:${offsetMinutes}`;
      };

      const cleanedData: any = {
        full_name: fullName,
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        role: formData.role.trim(),
        check_in: formData.check_in || null,
        check_out: formData.check_out || null,
        email: formData.email || null,
        phone: formData.phone || null,
        company: formData.company || null,
        hotel: formData.hotel || null,
        room_type: formData.room_type || null,
        confirmation: formData.confirmation || null,
        travel_days: formData.travel_days || null,
        arrival_flight: formData.arrival_flight || null,
        arrival_datetime: createISODateTime(formData.arrival_datetime),
        departure_flight: formData.departure_flight || null,
        departure_datetime: createISODateTime(formData.departure_datetime),
        travel_notes: formData.travel_notes || null,
        emergency_contact: formData.emergency_contact || null,
        emergency_phone: formData.emergency_phone || null,
        notes: formData.notes || null
      };

      if (!formData.is_own_record) {
        cleanedData.permission_level = formData.permission_level || null;
      }

      if (editingId) {
        await supabase
          .from('crew_members')
          .update(cleanedData)
          .eq('id', editingId);
      } else {
        const { error: insertError } = await supabase
          .from('crew_members')
          .insert({
            ...cleanedData,
            event_id: eventId,
            user_id: selectedUserId,
            invite_sent: true,
            invite_sent_at: new Date().toISOString()
          });

        if (insertError) {
          throw insertError;
        }

        const accessRole = formData.permission_level === 'editor' ? 'editor' : 'viewer';
        const { error: accessError } = await supabase
          .from('event_access')
          .insert({
            event_id: eventId,
            user_id: selectedUserId,
            role: accessRole,
          });

        if (accessError && !accessError.message?.includes('duplicate')) {
          throw new Error('Failed to grant event access: ' + accessError.message);
        }

        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session?.access_token) {
            throw new Error('No session token available');
          }

          const { data: eventData } = await supabase
            .from('events')
            .select('name')
            .eq('id', eventId)
            .single();

          const response = await fetch(
            `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-crew-addition`,
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                user_id: selectedUserId,
                event_id: eventId,
                event_name: eventData?.name || 'Event',
                crew_role: formData.role,
              }),
            }
          );

          if (!response.ok) {
            console.error('Failed to send notification email');
          }
        } catch (notificationError) {
          console.error('Notification error:', notificationError);
        }
      }

      await loadCrew();
      setShowForm(false);
      setEditingId(null);
      setSelectedUserId('');
      setSaving(false);
    } catch (error: any) {
      console.error('Error saving crew member:', error);
      alert('Failed to add crew member: ' + (error.message || 'Unknown error'));
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const crewMember = crew.find(m => m.id === deleteId);

      if (crewMember?.user_id) {
        const { error: accessError } = await supabase
          .from('event_access')
          .delete()
          .eq('event_id', eventId)
          .eq('user_id', crewMember.user_id);

        if (accessError) {
          console.error('Error removing event access:', accessError);
          alert('Failed to remove event access: ' + accessError.message);
          return;
        }
      }

      const { error: crewError } = await supabase
        .from('crew_members')
        .delete()
        .eq('id', deleteId);

      if (crewError) {
        console.error('Error removing crew member:', crewError);
        alert('Failed to remove crew member: ' + crewError.message);
        return;
      }

      await loadCrew();
      setDeleteId(null);
    } catch (error: any) {
      console.error('Error deleting crew member:', error);
      alert('An unexpected error occurred: ' + (error.message || 'Unknown error'));
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-zinc-500">Loading crew...</div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white">Crew List</h3>
            {permissions.canEdit && (
              <Button
                onClick={handleAddNew}
                className="bg-primary-500 hover:bg-primary-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Crew Member
              </Button>
            )}
          </div>

          {crew.length === 0 ? (
            <div className="p-12 text-center text-zinc-500">
              No crew members added yet
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {crew.map((member) => {
                const isExpanded = expandedId === member.id;
                const fullName = member.first_name && member.last_name
                  ? `${member.first_name} ${member.last_name}`
                  : member.name || member.full_name || 'No Name';
                const isOwnRecord = user?.id && member.user_id === user.id;
                const canEditMember = permissions.canEdit || isOwnRecord;
                const canDeleteMember = permissions.canEdit;

                return (
                  <div key={member.id} className="bg-zinc-900/30">
                    <div
                      className="flex items-center justify-between p-4 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(isExpanded ? null : member.id)}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <button className="text-zinc-400 hover:text-white flex-shrink-0">
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <h4 className="text-white font-medium">{fullName}</h4>
                            <span className="text-sm text-zinc-400">{member.role || 'No Position'}</span>
                            {member.company && (
                              <span className="text-sm text-zinc-500">• {member.company}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              member.permission_level === 'editor'
                                ? 'bg-primary-500/20 text-primary-400'
                                : 'bg-zinc-700 text-zinc-300'
                            }`}>
                              {member.permission_level === 'editor' ? 'Editor' : 'Viewer'}
                            </span>
                            {member.user_id && (
                              <div className="flex items-center gap-1 text-emerald-400">
                                <CheckCircle className="w-3 h-3" />
                                <span className="text-xs">Active</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {(canEditMember || canDeleteMember) && (
                        <div className="flex gap-2 ml-4">
                          {canEditMember && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(member);
                              }}
                              className="text-primary-400 hover:text-primary-300 transition-colors p-2"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                          )}
                          {canDeleteMember && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteId(member.id);
                              }}
                              className="text-red-400 hover:text-red-300 transition-colors p-2"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {isExpanded && (
                      <div className="px-4 pb-4 bg-zinc-900/50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-zinc-800/30 rounded-lg">
                          <div className="space-y-4">
                            <div className="flex items-start gap-3">
                              <User className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <h5 className="text-sm font-medium text-zinc-300 mb-2">Contact Information</h5>
                                {member.email && (
                                  <p className="text-sm text-white mb-1">
                                    <span className="text-zinc-500">Email:</span> {member.email}
                                  </p>
                                )}
                                {member.phone && (
                                  <p className="text-sm text-white mb-1">
                                    <span className="text-zinc-500">Phone:</span> {member.phone}
                                  </p>
                                )}
                                {member.company && (
                                  <p className="text-sm text-white mb-1">
                                    <span className="text-zinc-500">Company:</span> {member.company}
                                  </p>
                                )}
                                {!member.email && !member.phone && !member.company && (
                                  <p className="text-sm text-zinc-500">No contact info</p>
                                )}
                              </div>
                            </div>

                            {(member.hotel || member.check_in || member.check_out || member.room_type || member.confirmation) && (
                              <div className="flex items-start gap-3">
                                <Hotel className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-sm font-medium text-zinc-300 mb-2">Hotel</h5>
                                  {member.hotel && (
                                    <p className="text-sm text-white mb-1">{member.hotel}</p>
                                  )}
                                  {(member.check_in || member.check_out) && (
                                    <p className="text-sm text-zinc-400 mb-1">
                                      {member.check_in && `Check-in: ${member.check_in}`}
                                      {member.check_in && member.check_out && ' • '}
                                      {member.check_out && `Check-out: ${member.check_out}`}
                                    </p>
                                  )}
                                  {member.room_type && (
                                    <p className="text-sm text-zinc-400 mb-1">Room: {member.room_type}</p>
                                  )}
                                  {member.confirmation && (
                                    <p className="text-sm text-zinc-400">Confirmation: {member.confirmation}</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {member.emergency_contact && (
                              <div className="flex items-start gap-3">
                                <Phone className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-sm font-medium text-zinc-300 mb-2">Emergency Contact</h5>
                                  <p className="text-sm text-white mb-1">{member.emergency_contact}</p>
                                  {member.emergency_phone && (
                                    <p className="text-sm text-zinc-400">{member.emergency_phone}</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="space-y-4">
                            {(member.arrival_flight || member.arrival_datetime || member.departure_flight || member.departure_datetime) && (
                              <div className="flex items-start gap-3">
                                <Plane className="w-5 h-5 text-zinc-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h5 className="text-sm font-medium text-zinc-300 mb-2">Flight Information</h5>
                                  {member.arrival_flight && (
                                    <p className="text-sm text-white mb-1">
                                      <span className="text-zinc-500">Arrival:</span> {member.arrival_flight}
                                      {member.arrival_datetime && (
                                        <span className="text-zinc-400 ml-2">
                                          {format(parseISO(member.arrival_datetime), 'MMM d, h:mm a')}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                  {member.departure_flight && (
                                    <p className="text-sm text-white mb-1">
                                      <span className="text-zinc-500">Departure:</span> {member.departure_flight}
                                      {member.departure_datetime && (
                                        <span className="text-zinc-400 ml-2">
                                          {format(parseISO(member.departure_datetime), 'MMM d, h:mm a')}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                  {member.travel_notes && (
                                    <p className="text-sm text-zinc-400 mt-2">{member.travel_notes}</p>
                                  )}
                                </div>
                              </div>
                            )}

                            {member.travel_days && (
                              <div>
                                <h5 className="text-sm font-medium text-zinc-300 mb-1">Travel Days</h5>
                                <p className="text-sm text-white">{member.travel_days}</p>
                              </div>
                            )}

                            {member.notes && (
                              <div>
                                <h5 className="text-sm font-medium text-zinc-300 mb-1">Notes</h5>
                                <p className="text-sm text-zinc-400 whitespace-pre-wrap">{member.notes}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-1">
        {showForm ? (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-y-hidden overflow-x-visible sticky top-6">
            <div className="p-4 border-b border-zinc-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                {editingId ? 'Edit' : 'Add'} Crew Member
              </h3>
              <Button
                onClick={() => setShowForm(false)}
                variant="ghost"
                className="text-zinc-400 hover:text-white"
              >
                Close
              </Button>
            </div>

            <div className="p-6 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto overflow-x-visible">
              {!editingId && (
                <div>
                  <Label className="text-zinc-300 text-sm">Select User *</Label>
                  <Select
                    value={selectedUserId}
                    onValueChange={handleUserSelect}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue placeholder="Choose a registered user" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {registeredUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.full_name || user.email} {user.email && user.full_name && `(${user.email})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-zinc-500 mt-1.5">
                    Only registered users can be added to crew. Use Admin Users to create new accounts.
                  </p>
                </div>
              )}

              <div>
                <Label className="text-zinc-300 text-sm">Position *</Label>
                <Input
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Technical Director"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">First Name *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Last Name *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-300 text-sm">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-300 text-sm">Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="xxx.xxx.xxxx"
                    className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div>
                <Label className="text-zinc-300 text-sm">Company</Label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Company name"
                  className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                />
              </div>

              {!formData.is_own_record && (
                <div>
                  <Label className="text-zinc-300 text-sm">Portal Access Level</Label>
                  <Select
                    value={formData.permission_level}
                    onValueChange={(value) => setFormData({ ...formData, permission_level: value })}
                  >
                    <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                      <SelectValue placeholder="Select access level" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="viewer">Viewer (View Only)</SelectItem>
                      <SelectItem value="editor">Editor (Can Edit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="border-t border-zinc-800 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3">Travel & Hotel</h4>

                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Hotel</Label>
                    <Input
                      value={formData.hotel}
                      onChange={(e) => setFormData({ ...formData, hotel: e.target.value })}
                      placeholder="Hotel name"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-300 text-sm">Check-In</Label>
                      <Input
                        type="date"
                        value={formData.check_in}
                        onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                        className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white w-full"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Check-Out</Label>
                      <Input
                        type="date"
                        value={formData.check_out}
                        onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                        className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm">Room Type</Label>
                      <Select
                        value={formData.room_type}
                        onValueChange={(value) => setFormData({ ...formData, room_type: value })}
                      >
                        <SelectTrigger className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="Single">Single</SelectItem>
                          <SelectItem value="Double">Double</SelectItem>
                          <SelectItem value="Suite">Suite</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Confirmation #</Label>
                      <Input
                        value={formData.confirmation}
                        onChange={(e) => setFormData({ ...formData, confirmation: e.target.value })}
                        placeholder="#"
                        className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-300 text-sm">Travel Days</Label>
                    <Input
                      value={formData.travel_days}
                      onChange={(e) => setFormData({ ...formData, travel_days: e.target.value })}
                      placeholder="e.g., Mon-Fri"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3">Flight Information</h4>

                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Arrival Flight #</Label>
                    <Input
                      value={formData.arrival_flight}
                      onChange={(e) => setFormData({ ...formData, arrival_flight: e.target.value })}
                      placeholder="e.g., AA123"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 text-sm">Arrival Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={formData.arrival_datetime}
                      onChange={(e) => setFormData({ ...formData, arrival_datetime: e.target.value })}
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 text-sm">Departure Flight #</Label>
                    <Input
                      value={formData.departure_flight}
                      onChange={(e) => setFormData({ ...formData, departure_flight: e.target.value })}
                      placeholder="e.g., AA456"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 text-sm">Departure Date & Time</Label>
                    <Input
                      type="datetime-local"
                      value={formData.departure_datetime}
                      onChange={(e) => setFormData({ ...formData, departure_datetime: e.target.value })}
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>

                  <div>
                    <Label className="text-zinc-300 text-sm">Travel Notes</Label>
                    <textarea
                      value={formData.travel_notes}
                      onChange={(e) => setFormData({ ...formData, travel_notes: e.target.value })}
                      rows={2}
                      placeholder="Additional travel details..."
                      className="mt-1.5 w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3">Emergency Contact</h4>

                <div className="space-y-3">
                  <div>
                    <Label className="text-zinc-300 text-sm">Contact Name</Label>
                    <Input
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                      placeholder="Full name"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Contact Phone</Label>
                    <Input
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                      placeholder="Phone number"
                      className="mt-1.5 bg-zinc-800/50 border-zinc-700 text-white"
                    />
                  </div>
                </div>
              </div>

              <div className="border-t border-zinc-800 pt-4 mt-4">
                <Label className="text-zinc-300 text-sm">Notes</Label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Additional notes..."
                  className="mt-1.5 w-full bg-zinc-800/50 border border-zinc-700 text-white rounded-md px-3 py-2 text-sm"
                />
              </div>

              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white mt-6 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Add Crew Member'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-12 text-center sticky top-6">
            <p className="text-zinc-500">Select a crew member to edit or add a new one</p>
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Remove Crew Member?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will remove this crew member from the event roster and revoke their access to this event portal. They will no longer be able to view or edit any event information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteId(null)}
              className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Remove Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
